// components/OutletTypes.jsx
import React, { useState } from "react";
import "./OutletTypes.css";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle, ChevronRight } from "lucide-react";

// Import custom original images (copyright-free)
import restaurantImg from "../image/outlet-restaurant.png";
import cafeImg from "../image/outlet-cafe.png";
import bakeryImg from "../image/outlet-bakery.png";
import cloudKitchenImg from "../image/outlet-cloud-kitchen.png";
import iceCreamImg from "../image/outlet-icecream.png";
import foodCourtImg from "../image/outlet-foodcourt.png";

const outlets = [
  {
    id: "restaurant",
    title: "Restaurant",
    subtitle: "Fine Dine & Casual Dining",
    tagline: "Full-service dine-in billing with table management & KOT",
    image: restaurantImg,
    route: "/restaurantfeature",
    gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    accentColor: "#f97316",
    glowColor: "rgba(249, 115, 22, 0.25)",
    badge: "🍽️ Most Popular",
    stats: [
      { label: "Tables Managed", value: "100+" },
      { label: "KOT Speed", value: "< 2s" },
      { label: "Bill Print", value: "Instant" },
    ],
    features: [
      "Table & Floor Management",
      "Kitchen Order Tickets (KOT)",
      "Captain Ordering App",
      "Waiter Calling System",
      "Split & Merge Bills",
      "Multi-tax GST Reports",
    ],
  },
  {
    id: "cafe",
    title: "Cafe & Coffee Shop",
    subtitle: "Quick Counter Service",
    tagline: "Fast counter billing with product variations & add-ons",
    image: cafeImg,
    route: "/coffeefeature",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    accentColor: "#8b5cf6",
    glowColor: "rgba(139, 92, 246, 0.25)",
    badge: "☕ Trending",
    stats: [
      { label: "Order Speed", value: "3x" },
      { label: "Variants", value: "Unlimited" },
      { label: "Add-ons", value: "∞" },
    ],
    features: [
      "Item Variations & Add-ons",
      "Quick Counter Billing",
      "Customer Analytics",
      "Inventory Reports",
      "Online Order Integration",
      "Loyalty & Wallet Points",
    ],
  },
  {
    id: "bakery",
    title: "Bakery & Sweet Shop",
    subtitle: "Advance Orders & Central Kitchen",
    tagline: "Pre-orders, central kitchen management & delivery tracking",
    image: bakeryImg,
    route: "/bakeryfeature",
    gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
    accentColor: "#ec4899",
    glowColor: "rgba(236, 72, 153, 0.25)",
    badge: "🎂 Specialty",
    stats: [
      { label: "Advance Orders", value: "Yes" },
      { label: "Delivery Track", value: "Live" },
      { label: "Kitchen Sync", value: "Auto" },
    ],
    features: [
      "Advance Order Management",
      "Central Kitchen Control",
      "Delivery Tracking",
      "Inventory Management",
      "Business Website",
      "Customer Feedback",
    ],
  },
  {
    id: "cloud-kitchen",
    title: "Cloud Kitchen",
    subtitle: "Multi-Brand Virtual Brands",
    tagline: "Multi-brand ordering with live delivery & aggregator sync",
    image: cloudKitchenImg,
    route: "/cloudkitchenfeature",
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    accentColor: "#06b6d4",
    glowColor: "rgba(6, 182, 212, 0.25)",
    badge: "🚀 Scalable",
    stats: [
      { label: "Brands", value: "Multi" },
      { label: "Aggregators", value: "All" },
      { label: "Dashboard", value: "Unified" },
    ],
    features: [
      "Multiple Brand Management",
      "Online Order Reconciliation",
      "Central Kitchen Management",
      "Compact Reporting System",
      "Virtual Brand Control",
      "Delivery Partner Sync",
    ],
  },
  {
    id: "ice-cream",
    title: "Ice Cream & Desserts",
    subtitle: "Stock & Expiry Management",
    tagline: "Stock tracking with expiry alerts & quick billing",
    image: iceCreamImg,
    route: "/icecreamfeature",
    gradient: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    accentColor: "#22c55e",
    glowColor: "rgba(34, 197, 94, 0.25)",
    badge: "🍦 Seasonal",
    stats: [
      { label: "Stock Alerts", value: "Real-time" },
      { label: "Expiry Track", value: "Auto" },
      { label: "Quick Bill", value: "Yes" },
    ],
    features: [
      "Item Expiry Monitoring",
      "Stock Management",
      "Low Stock Alerts",
      "Quick Billing Counter",
      "Flavor Variations",
      "Sales Analytics",
    ],
  },
  {
    id: "food-court",
    title: "Food Court & Canteen",
    subtitle: "Counter-Wise QR Ordering",
    tagline: "Multi-counter billing with QR code ordering & unified reports",
    image: foodCourtImg,
    route: "/foodcourtfeature",
    gradient: "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)",
    accentColor: "#eab308",
    glowColor: "rgba(234, 179, 8, 0.25)",
    badge: "🏪 Enterprise",
    stats: [
      { label: "Counters", value: "50+" },
      { label: "QR Order", value: "Yes" },
      { label: "Reports", value: "Counter-wise" },
    ],
    features: [
      "Counter-Wise Billing",
      "QR Code Based Ordering",
      "Unified Dashboard",
      "Counter-Wise Reports",
      "Multi-Vendor Support",
      "Employee Management",
    ],
  },
];

const OutletTypes = () => {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <section className="section outlets-section" id="outlets">
      <div className="container">
        {/* Section Header */}
        <div className="outlets__header">
          <span className="outlets__badge">Outlet Types</span>
          <h2 className="section-title">
            Built for{" "}
            <span className="gradient-text">Every Food Business</span>
          </h2>
          <p className="section-subtitle">
            KadaiPOS delivers specialized billing configurations tailored for
            each outlet type — with unique features that perfectly match your
            workflow and scale your business.
          </p>
        </div>

        {/* Outlets Grid */}
        <div className="outlets__grid">
          {outlets.map((outlet) => (
            <div
              key={outlet.id}
              className={`outlets__card ${hoveredId === outlet.id ? "outlets__card--hovered" : ""}`}
              onMouseEnter={() => setHoveredId(outlet.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(outlet.route)}
              style={{ "--accent": outlet.accentColor, "--glow": outlet.glowColor }}
            >
              {/* Image */}
              <div className="outlets__card-image">
                <img src={outlet.image} alt={outlet.title} loading="lazy" />
                <div
                  className="outlets__card-overlay"
                  style={{ background: outlet.gradient }}
                />
                {/* Badge */}
                <span className="outlets__badge-pill">{outlet.badge}</span>
              </div>

              {/* Content */}
              <div className="outlets__card-body">
                <div className="outlets__card-meta">
                  <span className="outlets__card-subtitle">{outlet.subtitle}</span>
                </div>
                <h3 className="outlets__card-title">{outlet.title}</h3>
                <p className="outlets__card-tagline">{outlet.tagline}</p>

                {/* Stats Row */}
                <div className="outlets__stats">
                  {outlet.stats.map((stat, i) => (
                    <div key={i} className="outlets__stat">
                      <span
                        className="outlets__stat-value"
                        style={{ color: outlet.accentColor }}
                      >
                        {stat.value}
                      </span>
                      <span className="outlets__stat-label">{stat.label}</span>
                    </div>
                  ))}
                </div>

                {/* Feature Chips */}
                <div className="outlets__features">
                  {outlet.features.slice(0, 4).map((feat, i) => (
                    <span key={i} className="outlets__feature-chip">
                      <CheckCircle size={11} style={{ color: outlet.accentColor }} />
                      {feat}
                    </span>
                  ))}
                  {outlet.features.length > 4 && (
                    <span
                      className="outlets__feature-chip outlets__feature-more"
                      style={{ color: outlet.accentColor, borderColor: outlet.accentColor + "40" }}
                    >
                      +{outlet.features.length - 4} more
                    </span>
                  )}
                </div>

                {/* CTA */}
                <button
                  className="outlets__card-cta"
                  style={{ "--accent": outlet.accentColor }}
                >
                  Explore Features
                  <ArrowUpRight size={15} className="outlets__cta-icon" />
                </button>
              </div>

              {/* Glow border on hover */}
              <div
                className="outlets__card-glow"
                style={{ boxShadow: `0 0 0 1px ${outlet.accentColor}60, 0 20px 60px ${outlet.glowColor}` }}
              />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="outlets__bottom-cta">
          <p className="outlets__cta-text">
            Not sure which fits your business?
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Get a Free Consultation <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default OutletTypes;
